import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App.js';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.jsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import { Textarea } from './ui/textarea.jsx';
import { Badge } from './ui/badge.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs.jsx';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Users, 
  CreditCard, 
  Upload, 
  Eye, 
  Edit,
  Trash2,
  FileText,
  BarChart3,
  Calendar,
  DollarSign,
  Building2,
  Download,
  X,
  Save,
  ImageIcon
} from 'lucide-react';

const SupplierFileUploadDialog = ({ supplier, open, onOpenChange, onUploadSuccess }) => {
  const [files, setFiles] = useState([]);
  const [descriptions, setDescriptions] = useState({});
  const [uploading, setUploading] = useState(false);
  const { API } = useAuth();


  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    
    // Initialize descriptions
    const newDescriptions = {...descriptions};
    selectedFiles.forEach(file => {
      if (!newDescriptions[file.name]) {
        newDescriptions[file.name] = '';
      }
    });
    setDescriptions(newDescriptions);
  };

  const handleDescriptionChange = (fileName, value) => {
    setDescriptions(prev => ({
      ...prev,
      [fileName]: value
    }));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Veuillez sélectionner au moins un fichier');
      return;
    }

    setUploading(true);
    try {
      const uploadPromises = files.map(file => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('description', descriptions[file.name] || '');

        return axios.post(`${API}/suppliers/${supplier.id}/files`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      });

      await Promise.all(uploadPromises);
      toast.success('Fichiers téléchargés avec succès!');
      onUploadSuccess();
      onOpenChange(false);
      setFiles([]);
      setDescriptions({});
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Échec du téléchargement des fichiers');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fileName) => {
    setFiles(files.filter(f => f.name !== fileName));
    setDescriptions(prev => {
      const newDesc = {...prev};
      delete newDesc[fileName];
      return newDesc;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajouter des fichiers au fournisseur</DialogTitle>
          <DialogDescription>
            Téléchargez des fichiers pour "{supplier.name}"
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="files">Fichiers</Label>
            <Input 
              id="files" 
              type="file" 
              multiple 
              onChange={handleFileChange}
              disabled={uploading}
            />
          </div>

          {files.length > 0 && (
            <div className="space-y-3">
              <Label>Fichiers sélectionnés:</Label>
              {files.map((file, index) => (
                <div key={index} className="p-3 border rounded-md bg-slate-50">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium truncate max-w-xs">
                      {file.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(file.name)}
                      disabled={uploading}
                    >
                      ×
                    </Button>
                  </div>
                  <Textarea
                    placeholder="Description (optionnel)"
                    value={descriptions[file.name] || ''}
                    onChange={(e) => handleDescriptionChange(file.name, e.target.value)}
                    disabled={uploading}
                    className="text-sm"
                    rows={2}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleUpload}
            disabled={uploading || files.length === 0}
          >
            {uploading ? 'Téléchargement...' : 'Télécharger'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { API } = useAuth();
  
  const [project, setProject] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [payments, setPayments] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSupplierDetails, setShowSupplierDetails] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  
  // Form states
  const [supplierForm, setSupplierForm] = useState({ name: '' });
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: '',
    description: ''
  });
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingSupplierName, setEditingSupplierName] = useState('');
  const [showSupplierFileModal, setShowSupplierFileModal] = useState(false);
  const [selectedSupplierForUpload, setSelectedSupplierForUpload] = useState(null);
  const [supplierFiles, setSupplierFiles] = useState({});

  
  // Add state for editing project
const [showProjectEditModal, setShowProjectEditModal] = useState(false);
const [projectForm, setProjectForm] = useState({ name: '', description: '', initial_budget: '' });

// Add function to open project edit modal
const openProjectEditModal = () => {
  setProjectForm({
    name: project.name,
    description: project.description || '',
    initial_budget: project.initial_budget || ''
  });
  setShowProjectEditModal(true);
};

// Add function to handle project update
const handleUpdateProject = async (e) => {
  e.preventDefault();
  if (!projectForm.name.trim()) {
    toast.error('Le nom du projet est requis');
    return;
  }

  try {
    const response = await axios.put(`${API}/projects/${projectId}`, projectForm);
    setProject(response.data);
    setShowProjectEditModal(false);
    toast.success('Projet mis à jour avec succès !');
  } catch (error) {
    console.error('Error updating project:', error);
    toast.error('Échec de la mise à jour du projet');
  }
};

  // Add this useEffect to fetch files when supplier details are opened
  useEffect(() => {
    if (selectedSupplier && showSupplierDetails) {
      fetchSupplierFiles(selectedSupplier.id);
    }
  }, [selectedSupplier, showSupplierDetails]);

  // Add this function to fetch supplier files
  const fetchSupplierFiles = async (supplierId) => {
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/files`);
      setSupplierFiles(prev => ({
        ...prev,
        [supplierId]: response.data
      }));
    } catch (error) {
      console.error('Error fetching supplier files:', error);
      // Initialize empty array if no files exist
      setSupplierFiles(prev => ({
        ...prev,
        [supplierId]: []
      }));
    }
  };

  // Add this handler function
  const handleSupplierFileUpload = (supplier) => {
    setSelectedSupplierForUpload(supplier);
    setShowSupplierFileModal(true);
  };

  const handleSupplierFileUploadSuccess = () => {
    if (selectedSupplierForUpload) {
      fetchSupplierFiles(selectedSupplierForUpload.id);
    }
    toast.success('Fichiers ajoutés avec succès!');
  };

  // Add this function to handle file deletion
  const handleDeleteSupplierFile = async (supplierId, fileId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fichier ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/suppliers/${supplierId}/files/${fileId}`);
      
      // Update files state
      setSupplierFiles(prev => ({
        ...prev,
        [supplierId]: prev[supplierId].filter(file => file.id !== fileId)
      }));
      
      toast.success('Fichier supprimé avec succès !');
    } catch (error) {
      console.error('Error deleting supplier file:', error);
      toast.error('Échec de la suppression du fichier');
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId, API]); // Added API to dependency array

  const fetchProjectData = async () => {
  try {
    setLoading(true);
    
    // Fetch project details
    const projectResponse = await axios.get(`${API}/projects/${projectId}`);
    const projectData = projectResponse.data;
    
    // Set initial project data
    setProject(projectData);
    
    // Fetch suppliers
    const suppliersResponse = await axios.get(`${API}/projects/${projectId}/suppliers`);
    let updatedSuppliers = suppliersResponse.data;

    // Fetch payments for each supplier and calculate totals
    const paymentsData = {};
    let totalProjectPayments = 0;
    
    for (const supplier of updatedSuppliers) {
      try {
        const paymentsResponse = await axios.get(`${API}/suppliers/${supplier.id}/payments`);
        paymentsData[supplier.id] = paymentsResponse.data;

        // Calculate totals for this supplier
        const totalPaid = paymentsResponse.data.reduce(
          (sum, p) => sum + parseFloat(p.amount || 0),
          0
        );
        const paymentCount = paymentsResponse.data.length;

        // Add totals to supplier
        supplier.total_paid = totalPaid;
        supplier.payment_count = paymentCount;
        
        // Add to total project payments
        totalProjectPayments += totalPaid;
        
      } catch (error) {
        console.error(`Error fetching payments for supplier ${supplier.id}:`, error);
        paymentsData[supplier.id] = [];
        supplier.total_paid = 0;
        supplier.payment_count = 0;
      }
    }

    // Update state
    setSuppliers(updatedSuppliers);
    setPayments(paymentsData);

    // Fetch stats
    try {
      const statsResponse = await axios.get(`${API}/projects/${projectId}/stats`);
      
      // Calculate project balance if not provided by backend
      const initialBudget = parseFloat(projectData.initial_budget) || 0;
      const backendBalance = parseFloat(statsResponse.data.balance);
      
      // Use backend balance if available, otherwise calculate it
      const projectBalance = !isNaN(backendBalance) 
        ? backendBalance 
        : initialBudget - totalProjectPayments;
      
      // Update project with calculated balance
      setProject(prevProject => ({
        ...prevProject,
        balance: projectBalance
      }));
      
      // Set stats with calculated values
      setStats({
        ...statsResponse.data,
        total_suppliers: suppliersResponse.data.length,
        total_payments: Object.values(paymentsData).reduce((acc, curr) => acc + curr.length, 0),
        total_amount: totalProjectPayments,
        initial_budget: initialBudget,
        balance: projectBalance
      });
      
    } catch (error) {
      console.error('Error fetching stats:', error);
      
      // Calculate values locally if stats API fails
      const initialBudget = parseFloat(projectData.initial_budget) || 0;
      const totalPaymentsCount = Object.values(paymentsData).reduce((acc, curr) => acc + curr.length, 0);
      const projectBalance = initialBudget - totalProjectPayments;
      
      // Update project with calculated balance
      setProject(prevProject => ({
        ...prevProject,
        balance: projectBalance
      }));
      
      // Set default stats if API call fails
      setStats({
        total_suppliers: suppliersResponse.data.length,
        total_payments: totalPaymentsCount,
        total_amount: totalProjectPayments,
        initial_budget: initialBudget,
        balance: projectBalance
      });
    }
    
  } catch (error) {
    console.error('Error fetching project data:', error);
    toast.error('Échec du chargement des données du projet');
    navigate('/');
  } finally {
    setLoading(false);
  }
};

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    if (!supplierForm.name.trim()) {
      toast.error('Le nom du fournisseur est requis');
      return;
    }

    try {
      const response = await axios.post(`${API}/projects/${projectId}/suppliers`, supplierForm);
      setSuppliers([...suppliers, response.data]);
      setPayments({...payments, [response.data.id]: []}); // Initialize empty payments array for new supplier
      setSupplierForm({ name: '' });
      setShowSupplierModal(false);
      toast.success('Fournisseur ajouté avec succès !');
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast.error('Échec de l\'ajout du fournisseur');
    }
  };
const handleUpdateSupplier = async (e) => {
  e.preventDefault();
  try {
    const response = await axios.put(
      `${API}/projects/${projectId}/suppliers/${editingSupplier.id}`, 
      supplierForm
    );
    setSuppliers(suppliers.map(s => s.id === editingSupplier.id ? response.data : s));
    setSupplierForm({ name: '' });
    setEditingSupplier(null);
    setShowSupplierModal(false);
    toast.success('Fournisseur mis à jour avec succès !');
  } catch (error) {
    console.error('Error updating supplier:', error);
    console.error('Error response:', error.response?.data);
    toast.error('Échec de la mise à jour du fournisseur: ' + (error.response?.data?.detail || 'Erreur serveur'));
  }
};

const handleUpdateSupplierName = async () => {
  if (!editingSupplierName.trim()) {
    toast.error('Le nom du fournisseur est requis');
    return;
  }

  try {
    const response = await axios.put(
      `${API}/projects/${projectId}/suppliers/${selectedSupplier.id}`, 
      { name: editingSupplierName }
    );

    // Update suppliers state
    const updatedSuppliers = suppliers.map(s => 
      s.id === selectedSupplier.id ? { ...s, name: editingSupplierName } : s
    );
    setSuppliers(updatedSuppliers);

    // Update selected supplier
    setSelectedSupplier({ ...selectedSupplier, name: editingSupplierName });

    toast.success('Nom du fournisseur mis à jour avec succès !');
  } catch (error) {
    console.error('Error updating supplier name:', error);
    console.error('Error response:', error.response?.data);
    toast.error('Échec de la mise à jour du nom du fournisseur: ' + (error.response?.data?.detail || 'Erreur serveur'));
  }
};


const handleDeleteSupplier = async (supplierId) => {
  if (!window.confirm('Êtes-vous sûr ? Cela supprimera tous les paiements de ce fournisseur.')) {
    return;
  }

  try {
    // Use the correct endpoint that matches your backend
    await axios.delete(`${API}/projects/${projectId}/suppliers/${supplierId}`);
    setSuppliers(suppliers.filter(s => s.id !== supplierId));
    
    // Remove supplier from payments object
    const newPayments = {...payments};
    delete newPayments[supplierId];
    setPayments(newPayments);
    
    toast.success('Fournisseur supprimé avec succès !');
  } catch (error) {
    console.error('Error deleting supplier:', error);
    toast.error('Échec de la suppression du fournisseur');
  }
};
  // Add this function at the top of your component
  const saveFileToPublicUploads = async (file, paymentId) => {
    try {
      // Create a unique filename to avoid conflicts
      const fileExtension = file.name.split('.').pop();
      const fileName = `payment_${paymentId}_${Date.now()}.${fileExtension}`;
      const filePath = `/uploads/${fileName}`;
      
      // For now, we'll just return the file path
      // In a real implementation, you would need to:
      // 1. Send the file to your backend to save it
      // 2. Or use a service like Firebase Storage, AWS S3, etc.
      
      return filePath;
    } catch (error) {
      console.error('Error preparing file:', error);
      throw new Error('Failed to prepare file for upload');
    }
  };
const handleCreatePayment = async (e) => {
  e.preventDefault();
  if (!paymentForm.amount || !paymentForm.payment_method) {
    toast.error('Le montant et le mode de paiement sont requis');
    return;
  }

  try {
    let response;
    const amount = parseFloat(paymentForm.amount);

    if (isNaN(amount) || amount <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }

    // Create FormData for the request
    const formData = new FormData();
    formData.append('amount', amount);
    formData.append('payment_method', paymentForm.payment_method);
    formData.append('description', paymentForm.description || '');

    // Handle file upload
    if (selectedFile) {
      setUploading(true);
      formData.append('proof', selectedFile);
    }

    if (editingPayment) {
      // Update existing payment
      response = await axios.put(`${API}/payments/${editingPayment.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } else {
      // Create new payment
      response = await axios.post(`${API}/suppliers/${selectedSupplier.id}/payments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }

    // Update payments state
    const updatedPayments = payments[selectedSupplier.id] || [];
    if (editingPayment) {
      const index = updatedPayments.findIndex((p) => p.id === editingPayment.id);
      if (index !== -1) {
        updatedPayments[index] = response.data;
      }
    } else {
      updatedPayments.push(response.data);
    }

    setPayments({
      ...payments,
      [selectedSupplier.id]: updatedPayments,
    });

    // Update supplier totals
    const newTotal = updatedPayments.reduce(
      (sum, p) => sum + parseFloat(p.amount || 0),
      0
    );

    const updatedSuppliers = suppliers.map((s) => {
      if (s.id === selectedSupplier.id) {
        return {
          ...s,
          total_paid: newTotal,
          payment_count: updatedPayments.length,
        };
      }
      return s;
    });
    setSuppliers(updatedSuppliers);

    // Reset form
    setPaymentForm({ amount: '', payment_method: '', description: '' });
    setSelectedFile(null);
    setEditingPayment(null);
    setShowPaymentModal(false);
    toast.success(editingPayment ? 'Paiement mis à jour avec succès !' : 'Paiement ajouté avec succès !');
  } catch (error) {
    console.error('Error creating/updating payment:', error);
    toast.error(editingPayment ? 'Échec de la mise à jour du paiement' : 'Échec de l\'ajout du paiement');
  } finally {
    setUploading(false);
  }
};




  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      
      // Update payments state
      const updatedPayments = payments[selectedSupplier.id].filter(p => p.id !== paymentId);
      setPayments({
        ...payments,
        [selectedSupplier.id]: updatedPayments
      });

      // Update supplier totals
      const newTotal = updatedPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const updatedSuppliers = suppliers.map(s => {
        if (s.id === selectedSupplier.id) {
          return {
            ...s,
            total_paid: newTotal,
            payment_count: updatedPayments.length
          };
        }
        return s;
      });
      setSuppliers(updatedSuppliers);

      toast.success('Paiement supprimé avec succès !');
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Échec de la suppression du paiement');
    }
  };


  const openSupplierModal = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setSupplierForm({ name: supplier.name });
    } else {
      setEditingSupplier(null);
      setSupplierForm({ name: '' });
    }
    setShowSupplierModal(true);
  };

  const openPaymentModal = (supplier, payment = null) => {
    setSelectedSupplier(supplier);
    setEditingPayment(payment);
    
    if (payment) {
      setPaymentForm({
        amount: payment.amount.toString(),
        payment_method: payment.payment_method,
        description: payment.description || ''
      });
    } else {
      setPaymentForm({ amount: '', payment_method: '', description: '' });
    }
    
    setSelectedFile(null);
    setShowPaymentModal(true);
  };

  const openSupplierDetails = (supplier) => {
    setSelectedSupplier(supplier);
    setEditingSupplierName(supplier.name);
    setShowSupplierDetails(true);
  };

const downloadFile = (filename) => {
  if (filename) {
    const baseUrl = API.replace('/api', '');
    const url = `${baseUrl}/uploads/${filename}`;
    window.open(url, '_blank');
  } else {
    console.error('Invalid filename:', filename);
    toast.error('Fichier non disponible');
  }
};

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement des détails du projet...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Projet non trouvé</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-slate-600 hover:text-slate-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au Tableau de Bord
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">{project?.name}</h1>
                  <p className="text-sm text-slate-600">{project?.description || 'Aucune description'}</p>
                </div>
              </div>
            </div>

            <Button 
              variant="outline" 
              onClick={openProjectEditModal}
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifier le Projet
            </Button>
            
            <Button onClick={() => openSupplierModal()} className="bg-gradient-to-r from-emerald-600 to-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter Fournisseur
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <Dialog open={showProjectEditModal} onOpenChange={setShowProjectEditModal}>
  <DialogContent className="sm:max-w-md">
    <DialogHeader>
      <DialogTitle>Modifier le Projet</DialogTitle>
      <DialogDescription>
        Mettre à jour les informations du projet
      </DialogDescription>
    </DialogHeader>
    <form onSubmit={handleUpdateProject} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="project-name">Nom du Projet</Label>
        <Input
          id="project-name"
          placeholder="ex: Rénovation Bureau"
          value={projectForm.name}
          onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="project-initial_budget">Montant Initial (MAD)</Label>
        <Input
          id="project-initial_budget"
          type="number"
          step="0.01"
          placeholder="0.00"
          value={projectForm.initial_budget}
          onChange={(e) => setProjectForm({ ...projectForm, initial_budget: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="project-description">Description</Label>
        <Textarea
          id="project-description"
          placeholder="Description du projet..."
          value={projectForm.description}
          onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
          rows={3}
        />
      </div>
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowProjectEditModal(false)}
        >
          Annuler
        </Button>
        <Button type="submit">
          Mettre à jour le Projet
        </Button>
      </div>
    </form>
  </DialogContent>
</Dialog>
        {/* Project Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="glass hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Fournisseurs</p>
                  <p className="text-2xl font-bold text-slate-800">{suppliers.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

           <Card className="glass hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Montant Initial</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {(parseFloat(project.initial_budget) || 0).toLocaleString('fr-FR')} MAD
                  </p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Paiements</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {suppliers.reduce((sum, s) => sum + (parseFloat(s.total_paid) || 0), 0).toLocaleString('fr-FR')} MAD
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

            <Card className="glass hover-lift">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Solde Restant</p>
                    <p className={`text-2xl font-bold ${
                      (parseFloat(project.balance) || 0) < 0 ? 'text-red-600' : 'text-slate-800'
                    }`}>
                      {(parseFloat(project.balance) || 0).toLocaleString('fr-FR')} MAD
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

          <Card className="glass hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Transactions</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {suppliers.reduce((sum, s) => sum + (parseInt(s.payment_count) || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher des fournisseurs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/70"
            />
          </div>
        </div>

        {/* Suppliers List */}
        {filteredSuppliers.length === 0 ? (
          <Card className="glass text-center py-12">
            <CardContent>
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                {searchTerm ? 'Aucun fournisseur trouvé' : 'Aucun fournisseur pour le moment'}
              </h3>
              <p className="text-slate-600 mb-6">
                {searchTerm 
                  ? 'Essayez d\'ajuster vos termes de recherche'
                  : 'Ajoutez votre premier fournisseur pour commencer à suivre les paiements'
                }
              </p>
              {!searchTerm && (
                <Button onClick={() => openSupplierModal()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter Votre Premier Fournisseur
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {filteredSuppliers.map((supplier) => (
              <Card key={supplier.id} className="glass hover-lift">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl text-slate-800">{supplier.name}</CardTitle>
                      <CardDescription>
                        Ajouté le {new Date(supplier.created_at).toLocaleDateString('fr-FR')}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSupplierDetails(supplier)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir Détails
                      </Button>
                      
                      {/* Add this new button for file upload */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSupplierFileUpload(supplier)}
                        title="Ajouter des fichiers"
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        onClick={() => openPaymentModal(supplier)}
                        className="bg-gradient-to-r from-emerald-600 to-emerald-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter Paiement
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span>{(parseInt(supplier.payment_count) || 0)} paiements</span>
                      <span>•</span>
                      <span>Total: {(parseFloat(supplier.total_paid) || 0).toLocaleString('fr-FR')} MAD</span>
                    </div>
                  </div>

                  {/* Payment History */}
                  {payments[supplier.id] && payments[supplier.id].length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-slate-800 mb-3">Paiements Récents</h4>
                      <div className="space-y-2">
                        {payments[supplier.id].slice(-3).map((payment) => (
                          <div key={payment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                              <div>
                                <p className="font-medium text-slate-800">{(parseFloat(payment.amount) || 0).toLocaleString('fr-FR')} MAD</p>
                                <p className="text-sm text-slate-600">{payment.payment_method}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-slate-600">
                                {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                              </p>
                              {payment.proof_file_path && (
                                <Badge variant="outline" className="text-xs">
                                  <FileText className="w-3 h-3 mr-1" />
                                  Justificatif joint
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Supplier Modal */}
      <Dialog open={showSupplierModal} onOpenChange={setShowSupplierModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSupplier ? 'Modifier le Fournisseur' : 'Ajouter un Nouveau Fournisseur'}
            </DialogTitle>
            <DialogDescription>
              {editingSupplier ? 'Mettre à jour les informations du fournisseur' : 'Ajouter un nouveau fournisseur à ce projet'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingSupplier ? handleUpdateSupplier : handleCreateSupplier} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="supplier-name">Nom du Fournisseur</Label>
              <Input
                id="supplier-name"
                placeholder="ex: ABC Électricité, Plomberie Jean"
                value={supplierForm.name}
                onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowSupplierModal(false)}
              >
                Annuler
              </Button>
              <Button type="submit">
                {editingSupplier ? 'Mettre à jour' : 'Ajouter'} le Fournisseur
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPayment ? 'Modifier le Paiement' : 'Ajouter un Paiement'}</DialogTitle>
            <DialogDescription>
              {editingPayment ? 'Mettre à jour les informations du paiement' : 'Enregistrer un nouveau paiement'} pour {selectedSupplier?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePayment} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Montant</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment-method">Mode de Paiement</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner le mode de paiement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Espèces</SelectItem>
                  <SelectItem value="bank_transfer">Virement Bancaire</SelectItem>
                  <SelectItem value="check">Chèque</SelectItem>
                  <SelectItem value="credit_card">Carte de Crédit</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optionnel)</Label>
              <Textarea
                id="description"
                placeholder="Description du paiement ou notes..."
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="proof">Justificatif de Paiement (Optionnel)</Label>
              <div className="flex items-center gap-2">
              <Input
                id="proof"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setSelectedFile(e.target.files[0]);
                  }
                }}
                className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
              />
              </div>
              {selectedFile && (
                <p className="text-xs text-slate-600">
                  Fichier sélectionné: {selectedFile.name}
                </p>
              )}
              {editingPayment && editingPayment.proof_file_path && !selectedFile && (
                <p className="text-xs text-slate-600">
                  Fichier actuel: {editingPayment.proof_file_path}
                </p>
              )}
              <p className="text-xs text-slate-500">Fichiers PDF, JPG ou PNG uniquement</p>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowPaymentModal(false);
                  setEditingPayment(null);
                }}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={uploading}>
                {uploading ? 'Téléchargement...' : (editingPayment ? 'Mettre à jour le Paiement' : 'Ajouter le Paiement')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Supplier Details Modal */}
      <Dialog open={showSupplierDetails} onOpenChange={setShowSupplierDetails}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl">Détails du Fournisseur</DialogTitle>
                <DialogDescription>
                  Voir et gérer toutes les informations de ce fournisseur
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowSupplierDetails(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>

          {selectedSupplier && (
            <div className="space-y-6">
              {/* Supplier Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Informations du Fournisseur
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <Input
                      value={editingSupplierName}
                      onChange={(e) => setEditingSupplierName(e.target.value)}
                      className="flex-1"
                      placeholder="Nom du fournisseur"
                    />
                    <Button onClick={handleUpdateSupplierName} size="sm">
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4 p-4 bg-slate-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-slate-800">{selectedSupplier.payment_count || 0}</p>
                      <p className="text-sm text-slate-600">Total Paiements</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{(parseFloat(selectedSupplier.total_paid) || 0).toLocaleString('fr-FR')} MAD</p>
                      <p className="text-sm text-slate-600">Montant Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedSupplier.total_paid > 0 && selectedSupplier.payment_count > 0
                          ? Math.round((parseFloat(selectedSupplier.total_paid) || 0) / (parseInt(selectedSupplier.payment_count) || 1))
                          : 0} MAD
                      </p>
                      <p className="text-sm text-slate-600">Paiement Moyen</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment History */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Historique des Paiements
                    </CardTitle>
                    <Button 
                      onClick={() => openPaymentModal(selectedSupplier)}
                      size="sm"
                      className="bg-gradient-to-r from-emerald-600 to-emerald-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter Paiement
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {payments[selectedSupplier.id] && payments[selectedSupplier.id].length > 0 ? (
                    <div className="space-y-4">
                      {payments[selectedSupplier.id].map((payment) => (
                        <div key={payment.id} className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                              <div>
                                <p className="font-semibold text-lg text-slate-800">
                                  {(parseFloat(payment.amount) || 0).toLocaleString('fr-FR')} MAD
                                </p>
                                <p className="text-sm text-slate-600">
                                  {payment.payment_method} • {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openPaymentModal(selectedSupplier, payment)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeletePayment(payment.id)}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {payment.description && (
                            <div className="mb-3">
                              <p className="text-sm text-slate-700 bg-slate-100 p-2 rounded">
                                {payment.description}
                              </p>
                            </div>
                          )}

                          {payment.proof_file_path && (
                            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                              <div className="flex items-center gap-2">
                                {payment.proof_file_path.toLowerCase().includes('.pdf') ? (
                                  <FileText className="w-5 h-5 text-red-600" />
                                ) : (
                                  <ImageIcon className="w-5 h-5 text-blue-600" />
                                )}
                                <div>
                                  <p className="text-sm font-medium text-slate-800">
                                    Fichier Joint
                                  </p>
                                  <p className="text-xs text-slate-600">
                                    {payment.proof_file_path}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => downloadFile(payment.proof_file_path)}
                                className="ml-auto"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CreditCard className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 mb-4">Aucun paiement enregistré pour le moment</p>
                      <Button 
                        onClick={() => openPaymentModal(selectedSupplier)}
                        className="bg-gradient-to-r from-emerald-600 to-emerald-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter le Premier Paiement
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Fichiers Associés
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {supplierFiles[selectedSupplier.id] && supplierFiles[selectedSupplier.id].length > 0 ? (
                    <div className="space-y-3">
                      {supplierFiles[selectedSupplier.id].map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                          <div className="flex items-center gap-3">
                            {file.original_name.toLowerCase().includes('.pdf') ? (
                              <FileText className="w-5 h-5 text-red-600" />
                            ) : (
                              <ImageIcon className="w-5 h-5 text-blue-600" />
                            )}
                            <div>
                              <p className="font-medium text-slate-800">{file.original_name}</p>
                              <p className="text-xs text-slate-600">
                                {new Date(file.uploaded_at).toLocaleDateString('fr-FR')} • 
                                {(file.file_size / 1024).toFixed(1)} KB
                              </p>
                              {file.description && (
                                <p className="text-xs text-slate-500 mt-1">{file.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadFile(file.file_name)}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteSupplierFile(selectedSupplier.id, file.id)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600">Aucun fichier associé à ce fournisseur</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedSupplierForUpload && (
        <SupplierFileUploadDialog
          supplier={selectedSupplierForUpload}
          open={showSupplierFileModal}
          onOpenChange={setShowSupplierFileModal}
          onUploadSuccess={handleSupplierFileUploadSuccess}
        />
      )}
    </div>
  );
};

export default ProjectDetails;